import { useState } from 'react';
import styled from 'styled-components';
import { Button } from '@shared/ui/Button';
import { Modal } from '@shared/ui/Modal';

type Severity = 'critical' | 'major' | 'minor';

interface Initial {
  category?: string;
  severity?: Severity;
  description?: string;
  proposed?: string;
}

interface Props {
  open: boolean;
  mode: 'add' | 'edit';
  scope: 'contract' | 'claim';
  initial?: Initial;
  onClose: () => void;
  onSubmit: (data: {
    category: string;
    severity: Severity;
    description: string;
    /** Для договора — proposed_change; для иска — proposed_action. */
    proposed?: string;
  }) => Promise<void> | void;
}

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Select = styled.select`
  padding: 8px 12px;
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
`;

const Textarea = styled.textarea`
  padding: 8px 12px;
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  min-height: 90px;
  resize: vertical;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(5)};
`;

const Hint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  padding: 10px;
  border-left: 2px solid ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.radius.sm};
`;

const CONTRACT_CATEGORIES: Record<string, string> = {
  subject: 'Предмет',
  price: 'Цена',
  terms: 'Сроки',
  liability: 'Ответственность',
  warranty: 'Гарантии',
  force_majeure: 'Форс-мажор',
  jurisdiction: 'Подсудность',
  other: 'Другое',
};

const CLAIM_CATEGORIES: Record<string, string> = {
  amount: 'Сумма требования',
  grounds: 'Основания',
  deadlines: 'Сроки',
  evidence: 'Доказательства',
  procedure: 'Процедура',
  jurisdiction: 'Подсудность',
  other: 'Другое',
};

export function ManualRiskModal({ open, mode, scope, initial, onClose, onSubmit }: Props) {
  const cats = scope === 'contract' ? CONTRACT_CATEGORIES : CLAIM_CATEGORIES;
  const proposedLabel =
    scope === 'contract'
      ? 'Предлагаемая редакция (опционально)'
      : 'Предлагаемое действие (опционально)';

  const [category, setCategory] = useState(initial?.category ?? Object.keys(cats)[0]);
  const [severity, setSeverity] = useState<Severity>(initial?.severity ?? 'major');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [proposed, setProposed] = useState(initial?.proposed ?? '');
  const [submitting, setSubmitting] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!description.trim()) return;
    setSubmitting(true);
    try {
      await onSubmit({
        category,
        severity,
        description: description.trim(),
        proposed: proposed.trim() || undefined,
      });
      onClose();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={mode === 'add' ? 'Добавить свой риск' : 'Редактировать риск'}
    >
      <form onSubmit={submit}>
        {mode === 'add' && (
          <Hint>
            LLM пропустила этот риск — добавьте его вручную. Запись попадёт в память агента и
            на следующих похожих документах будет учитываться как прецедент.
          </Hint>
        )}

        <Field>
          Категория
          <Select value={category} onChange={(e) => setCategory(e.target.value)}>
            {Object.entries(cats).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </Select>
        </Field>

        <Field>
          Степень важности
          <Select value={severity} onChange={(e) => setSeverity(e.target.value as Severity)}>
            <option value="critical">Критическое</option>
            <option value="major">Важное</option>
            <option value="minor">Незначительное</option>
          </Select>
        </Field>

        <Field>
          Описание риска
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Что именно проблематично, чем грозит, ссылки на пункты документа…"
            required
          />
        </Field>

        <Field>
          {proposedLabel}
          <Textarea
            value={proposed}
            onChange={(e) => setProposed(e.target.value)}
            placeholder={
              scope === 'contract'
                ? 'Например: «изложить п. 5.3 в редакции…» или ссылка на типовую форму.'
                : 'Например: «направить запрос на дополнительные документы», «снизить сумму до X»…'
            }
          />
        </Field>

        <Footer>
          <Button type="button" $variant="secondary" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" disabled={submitting || !description.trim()}>
            {submitting ? 'Сохранение…' : mode === 'add' ? 'Добавить' : 'Сохранить'}
          </Button>
        </Footer>
      </form>
    </Modal>
  );
}

// Используются для бейджа «manual» / «правлено юристом» в карточках.
export function isManualSource(source?: string | null): boolean {
  return source === 'manual';
}

export function isLawyerCorrected(source?: string | null): boolean {
  return source === 'lawyer_corrected';
}
